import { useState, useRef, useEffect } from 'react';
import { FaTimes, FaMinus, FaPaperPlane } from 'react-icons/fa';
import { useChat } from './ChatContext';
import { getQuickActions, generateAIResponse } from './chatHelpers.jsx';

const ChatWindow = ({ pageContext = 'default' }) => {
  const { 
    isOpen, 
    setIsOpen, 
    isMinimized, 
    setIsMinimized, 
    messages, 
    isTyping,
    addMessage,
    sendAIResponse
  } = useChat();
  
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    addMessage(inputValue, 'user');
    const response = generateAIResponse(inputValue);
    setInputValue('');
    sendAIResponse(response);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleQuickAction = (query) => {
    setInputValue(query);
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const minimizeChat = () => {
    setIsMinimized(true);
  };

  const quickActions = getQuickActions(pageContext);

  if (!isOpen) return null;

  return (
    <div className={`chat-widget-window ${isMinimized ? 'minimized' : ''}`}>
      {/* Header */}
      <div className="chat-widget-header">
        <div className="d-flex align-items-center">
          <img 
            src="/images/head-shot-246x300.jpg" 
            alt="Brad Cummins"
            className="agent-avatar"
            style={{ objectFit: 'cover' }}
          />
          <div className="ms-2">
            <div className="fw-bold text-white">Brad Cummins</div>
            <div className="chat-status">
              <span className="status-dot"></span>
              Online • AI Assistant
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button 
            className="chat-control-btn"
            onClick={minimizeChat}
            aria-label="Minimize"
          >
            <FaMinus />
          </button>
          <button 
            className="chat-control-btn"
            onClick={closeChat}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      {/* Messages Body */}
      {!isMinimized && (
        <>
          <div className="chat-widget-body">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-message ${message.sender === 'ai' ? 'ai-message' : 'user-message'}`}
              >
                {message.sender === 'ai' && (
                  <img 
                    src="/images/head-shot-246x300.jpg" 
                    alt="Brad Cummins"
                    className="message-avatar"
                    loading="lazy"
                    decoding="async"
                    style={{ objectFit: 'cover' }}
                  />
                )}
                <div className="message-bubble">
                  <div className="message-text">{message.text}</div>
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="chat-message ai-message">
                <img 
                  src="/images/head-shot-246x300.jpg" 
                  alt="Brad Cummins"
                  className="message-avatar"
                  loading="lazy"
                  decoding="async"
                  style={{ objectFit: 'cover' }}
                />
                <div className="message-bubble">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && quickActions && quickActions.length > 0 && (
            <div className="chat-quick-actions">
              {quickActions.map((action, index) => (
                <button 
                  key={index}
                  className="quick-action-btn" 
                  onClick={() => handleQuickAction(action.query)}
                >
                  {action.emoji && <span className="me-2">{action.emoji}</span>}
                  {action.text}
                </button>
              ))}
            </div>
          )}

          {/* Input Footer */}
          <div className="chat-widget-footer">
            <input
              type="text"
              className="chat-input"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button 
              className="chat-send-btn"
              onClick={handleSendMessage}
              disabled={!inputValue.trim()}
            >
              <FaPaperPlane />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatWindow;

